import type { AdSlot as AdSlotPayload } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { cn } from '@/components/ui';

/**
 * PRD §17 placements, rendered as **reserved-height boxes** (Phase 8 task list: "so no
 * layout shift appears later").
 *
 * The height comes from the placement's own dimensions in the database, not from the
 * creative, so the space is identical whether an ad is sold or not — which is the only
 * way Phase 13 can start serving creatives without moving the page under a reader mid-
 * sentence. Impression and click tracking is Phase 13's; this does not even link yet.
 */
export function AdSlot({ slot, className }: { slot: AdSlotPayload; className?: string }) {
  const { t } = useLocale();

  return (
    <div
      className={cn(
        'mx-auto flex w-full flex-col items-center justify-center rounded-sm border border-dashed border-[#d4d7db] bg-soft text-2xs font-semibold tracking-widest text-muted',
        slot.isMobileOnly && 'md:hidden',
        className,
      )}
      // Reserving the exact box the creative will occupy is the whole point of the slot.
      style={{ height: slot.height, maxWidth: slot.width }}
      data-placement={slot.placement}
    >
      {/* No `opacity` on the label: 9px `--muted` faded to 70% lands at 2.7:1 on the
          slot's own background, which axe rightly fails. Full strength is 5.3:1, and the
          label still reads as the quiet thing it is next to the creative. */}
      <span className="text-[9px] tracking-[2px]">{t('meta.advertisement')}</span>
      {slot.advertisement?.altText ?? `${slot.width}×${slot.height}`}
    </div>
  );
}
